import { CaseAlert, ProductReturnRequirement, TechnicalPreference } from 'API';
import _ from 'lodash';
import React, { PropsWithChildren, useState } from 'react';
import { CreatedOrder } from 'shared/models';

/**
 * Represents the context shape for the case detail module.
 */
interface Context {
  caseLoading: boolean;
  caseDetails?: CreatedOrder;
  technicalPreferences: TechnicalPreference[];
  caseAlerts: CaseAlert[];
  productReturnRequirements: ProductReturnRequirement[];
}

/**
 * Represents the context actions for the case detail module.
 */
interface ActionsContext {
  setCaseLoading: (isLoading: boolean) => void;
  setCaseDetails: (caseDetails: CreatedOrder) => void;
  setTechnicalPreferences: (technicalPreferences: TechnicalPreference[]) => void;
  setCaseAlerts: (caseAlerts: CaseAlert[]) => void;
  setProductReturnRequirements: (productReturnRequirements: ProductReturnRequirement[]) => void;
}

/**
 * Initial context for the case detail module actions.
 */
export const CaseDetailModuleActionsInitialContext: ActionsContext = {
  setCaseLoading: (isLoading: boolean) => {
    return isLoading;
  },
  setCaseDetails: (caseData: CreatedOrder) => {
    return caseData;
  },
  setTechnicalPreferences: (technicalPreferences: TechnicalPreference[]) => {
    return technicalPreferences;
  },
  setCaseAlerts: () => {
    return;
  },
  setProductReturnRequirements: () => {
    return;
  },
};

/**
 * Initial state for the case detail module context.
 */
const initialState: Context = {
  caseLoading: false,
  technicalPreferences: [],
  caseAlerts: [],
  productReturnRequirements: [],
};

/**
 * Context for managing the state of the case detail module.
 */
export const CaseDetailModuleContext = React.createContext<Context>(_.cloneDeep(initialState));
export const CaseDetailModuleActionsContext = React.createContext<ActionsContext>(
  CaseDetailModuleActionsInitialContext
);

/**
 * Provider component for the case detail module context.
 * @param children - Child components.
 * @param props - Additional properties for context.
 */
export const CaseDetailModuleProvider: React.FC<PropsWithChildren<Partial<Context>>> = ({ children, ...props }) => {
  const initialStateCopy = _.cloneDeep(initialState);
  const [caseLoading, setCaseLoading] = useState<boolean>(props.caseLoading || initialStateCopy.caseLoading);
  const [caseDetails, setCaseDetails] = useState<CreatedOrder | undefined>(
    props.caseDetails || initialStateCopy.caseDetails
  );
  const [technicalPreferences, setTechnicalPreferences] = useState<TechnicalPreference[]>([]);
  const [caseAlerts, setCaseAlerts] = useState<CaseAlert[]>([]);
  const [productReturnRequirements, setProductReturnRequirements] = useState<ProductReturnRequirement[]>([]);

  return (
    <>
      <CaseDetailModuleContext.Provider
        value={{ caseLoading, caseDetails, technicalPreferences, caseAlerts, productReturnRequirements }}
      >
        <CaseDetailModuleActionsContext.Provider
          value={{
            setCaseLoading,
            setCaseDetails,
            setTechnicalPreferences,
            setCaseAlerts,
            setProductReturnRequirements,
          }}
        >
          {children}
        </CaseDetailModuleActionsContext.Provider>
      </CaseDetailModuleContext.Provider>
    </>
  );
};

/**
 * Custom hook for accessing case detail module context and actions.
 * @returns Context and actions for the case detail module.
 */
export const useCaseDetail = () => {
  const { caseLoading, caseDetails, productReturnRequirements } = React.useContext(CaseDetailModuleContext);
  const { setCaseLoading, setCaseDetails, setProductReturnRequirements } =
    React.useContext(CaseDetailModuleActionsContext);

  return {
    caseLoading,
    caseDetails,
    productReturnRequirements,
    setCaseLoading,
    setCaseDetails,
    setProductReturnRequirements,
  };
};
